import { PUBLIC_STORYBLOK_ACCESS_TOKEN } from '$app/env/public'
import {
	storyblokInit,
	apiPlugin,
	type StoryblokRichTextOptions,
	type StoryblokRichTextNode,
	renderRichText,
	useStoryblokBridge as bridge,
	StoryblokComponent as Block,
	storyblokEditable as editable,
	useStoryblokApi as api
} from '@storyblok/svelte'
import type { Component } from 'svelte'
import type { StoryblokRichtext, Page } from './components.schema.build.js'
import type { Template } from './types.js'
import { SvelteMap } from 'svelte/reactivity'

export interface DatasourceEntry {
	id: number
	name: string
	value: string
	dimension_value: string | null
}

/** Correct the type information for the RichtextStoryblok */
export interface RichtextStoryblok extends StoryblokRichtext {
	attrs?: {
		body: Page['blocks']
	}
	content?: Array<RichtextStoryblok>
	marks?: Array<RichtextStoryblok>
}

export class Storyblok<T extends Template> {
	story = $state<T | null>(null)
	static templates = new SvelteMap<string, Component>()
	static blocks = new SvelteMap<string, Component>()

	static get relations() {
		return ['event.related_events', 'card_lite.populate', 'featured.stories', 'news.related', 'blog.related']
	}

	static is_preview(url: URL): boolean {
		return url.searchParams.has('_storyblok')
	}

	constructor(story: T) {
		this.story = story

		$effect(() => {
			if (!story) return
			this.story = story

			bridge(story.id, new_page => (this.story = new_page as T), {
				resolveLinks: 'url',
				resolveRelations: Storyblok.relations
			})
		})
	}

	static async init() {
		const components = import.meta.glob(['#blocks/*.svelte', '#templates/*.svelte'], {
			eager: true
		}) as Record<string, { default: Component }>

		for (const path in components) {
			const name = path.split('/').pop()?.replace('.svelte', '') ?? ''

			if (!name) throw new Error(`Couldn't extract name from path: ${path}`)
			if (path.includes('/blocks/')) Storyblok.blocks.set(name, components[path].default)
			else Storyblok.templates.set(name, components[path].default)
		}

		// News uses the blog template
		Storyblok.templates.set('news', Storyblok.templates.get('blog')!)

		return storyblokInit({
			accessToken: PUBLIC_STORYBLOK_ACCESS_TOKEN,
			use: [apiPlugin],
			components: Object.fromEntries(Storyblok.blocks)
		})
	}

	static get api() {
		return api
	}

	static get bridge() {
		return bridge
	}

	static get editable() {
		return editable
	}

	static get Block() {
		return Block
	}
}

export { api, bridge, editable, Block }

export function richtext<T = string>(content: RichtextStoryblok, options?: StoryblokRichTextOptions<T>) {
	return renderRichText(content as StoryblokRichTextNode<T>, options)
}
